import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatStepperModule } from '@angular/material/stepper';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { environment } from '@environments/environment';
import { ByProductItem, CreatePriceRequest, LocalizedString, ProductType } from '@hjc/masterdata-types';
import { TranslateModule } from '@ngx-translate/core';
import { AddPriceComponent, AddPriceResult } from '@pages/pricing/components/add-price/add-price.component';
import { IntegrationConfig } from '@services/integration-manager.service';
import { PartnersService } from '@services/partners.service';
import { BasePartnerCreationComponent } from '../base-item-creation.component';
import { IntegrationComponent } from '../components/integration/integration.component';
import { PriceListComponent } from '../components/price-list/price-list.component';
import { CreatePageHeaderComponent } from '../create-page-header/create-page-header.component';
import { BYPRODUCT_INTEGRATION_CONFIGS } from '../partner-integration.configs';

@Component({
  selector: 'md-create-byproduct',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatStepperModule,
    MatButtonModule,
    MatProgressSpinnerModule,
    MatIconModule,
    CreatePageHeaderComponent,
    TranslateModule,
    IntegrationComponent,
    AddPriceComponent,
    PriceListComponent
  ],
  templateUrl: './create-byproduct.component.html',
  styleUrl: './create-byproduct.component.scss',
  schemas: []
})
export class CreateByproductComponent extends BasePartnerCreationComponent<ByProductItem> implements OnInit, OnDestroy {
  readonly helos20CreateUrl = environment.helos20.baseUrl + '/admin/by-products/manage/edit/byproduct/new';

  private readonly TOTAL_STEPS = 3;
  private readonly partnersService = inject(PartnersService);
  private readonly sanitizer = inject(DomSanitizer);

  // Sanitized URL to prevent constant re-rendering
  readonly safeHelosUrl: SafeResourceUrl = this.sanitizer.bypassSecurityTrustResourceUrl(this.helos20CreateUrl);

  protected override getPartnerIntegrationConfigs(): IntegrationConfig[] {
    return BYPRODUCT_INTEGRATION_CONFIGS;
  }

  protected override getTotalSteps(): number {
    return this.TOTAL_STEPS;
  }

  protected override getDetailsRoute(partnerId: string): string[] {
    return ['/app/view/products/byproducts', partnerId];
  }

  protected override async fetchDataForPricing(): Promise<void> {
    this.isLoading.set(true);
    try {
      const partnersResponse = await this.partnersService.getSlaughterhousePartners();

      // Filter out partners that already have prices for this byproduct (if we have one)
      let availablePartners = partnersResponse.data;
      if (this.primaryItemData?.id) {
        try {
          const existingPrices = await this.pricingService.getPricingForByproduct(this.primaryItemData.id);
          const existingPartnerIds = new Set(existingPrices.data?.map(p => p.partnerId) || []);
          availablePartners = partnersResponse.data.filter(partner => !existingPartnerIds.has(partner.id));
        } catch (error) {
          console.warn('Could not fetch existing prices for filtering:', error);
        }
      }

      this.priceDataForChild.set({
        items: availablePartners,
        measure: this.pricingService.measures
      });
      this.notificationService.info(this.translateService.instant('NOTIFICATIONS.SUCCESS.PRICING_DATA_PREPARED'));
    } finally {
      this.isLoading.set(false);
    }
  }

  protected override createPriceObject(input: AddPriceResult): CreatePriceRequest | undefined {
    if (!this.primaryItemData?.id) {
      this.errorService.showError('BYPRODUCT.ERRORS.NO_PRODUCT');
      return undefined;
    }
    return {
      name: input.itemName,
      productType: 'byProduct' as ProductType,
      productId: this.primaryItemData.id,
      partnerType: input.itemType as 'haulier' | 'slaughterhouse',
      partnerId: input.itemId,
      rates: [{ rate: input.price, validFrom: input.validFrom.toISOString() }],
      unit: input.unit,
      productName: this.getItemNameLocalized(this.primaryItemData?.['name'] as string | LocalizedString | undefined),
      partnerName: input.itemName
    } satisfies CreatePriceRequest;
  }

  localizedByProductNameForDisplay(): string {
    if (!this.createdItem?.name) return '';
    const name = this.createdItem.name;
    if (typeof name === 'string') return name;
    const lang = this.currentLang() as 'fi' | 'en' | 'sv';
    return name[lang] || '';
  }

  onDeletePrice(price: AddPriceResult): void {
    this.priceListSignal.update(prices => prices.filter(p => p.itemId !== price.itemId || p.validFrom.getTime() !== price.validFrom.getTime()));
    this.notificationService.info(this.translateService.instant('NOTIFICATIONS.SUCCESS.PRICE_REMOVED'));
  }

  override onPriceAdded(result: AddPriceResult): void {
    this.priceListSignal.update(prices => [...prices, result]);
    this.notificationService.info(this.translateService.instant('NOTIFICATIONS.SUCCESS.PRICING_DATA_PREPARED'));
  }

  async onDone(): Promise<void> {
    try {
      this.isLoading.set(true);
      await this.onSaveDone();
      this.notificationService.success(this.translateService.instant('NOTIFICATIONS.SUCCESS.CREATION_COMPLETE'));
    } finally {
      this.isLoading.set(false);
    }
  }
}
